<!-- Home.vue - Complete Redesign with Enhanced Animations -->
<template>
  <div class="fm-container">
    <!-- Background Image with Blur -->
    <div class="background-image" :class="{ 'blur-active': isLoading }"></div>
    
    <!-- Loading Overlay -->
    <transition name="loading-overlay">
      <div v-if="isLoading" class="loading-overlay">
        <div class="loading-card">
          <div class="loading-animation">
            <div class="loading-ring">
              <div class="ring"></div>
              <div class="ring"></div>
              <div class="ring"></div>
              <div class="loading-logo">
                <i class="fas fa-chart-line"></i>
              </div>
            </div>
          </div>
          <div class="loading-text">
            <h3>{{ loadingMessage }}</h3>
            <div class="loading-dots">
              <span class="dot"></span>
              <span class="dot"></span>
              <span class="dot"></span>
            </div>
          </div>
          <div class="loading-progress">
            <div class="progress-bar" :style="{ width: loadingProgress + '%' }"></div>
          </div>
          <div class="loading-tips">
            <p class="tip-text">{{ currentTip }}</p>
          </div>
        </div>
      </div>
    </transition>
    
    <!-- Main Content -->
    <div class="content-wrapper" :class="{ 'blur-content': isLoading }">
      
      <!-- Modern Header with User Info -->
      <transition appear name="fade-slide-down">
        <div class="dashboard-header">
          <div class="header-left">
            <div class="greeting-badge">
              <span class="greeting-icon">👋</span>
              <div>
                <h1 class="dashboard-title">Dashboard</h1>
                <p class="dashboard-subtitle">Welcome back, {{ userDetails.username || 'User' }}</p>
              </div>
            </div>
          </div>
          <div class="header-right">
            <div class="user-role-badge">
              <i class="fas fa-user-shield"></i>
              <span>{{ formatUserRole(userDetails.userrole) }}</span>
            </div>
            <div class="date-badge">
              <i class="far fa-calendar-alt"></i>
              <span>{{ currentDate }}</span>
            </div>
          </div>
        </div>
      </transition>

      <!-- Modern Glassmorphism Tab Bar with Enhanced Animations -->
      <div class="tab-bar-container">
        <div class="tab-bar">
          <button 
            v-for="tab in availableTabs" 
            :key="tab.key"
            class="tab-btn"
            :class="{ active: activeTab === tab.key }"
            @click="switchTab(tab.key)"
          >
            <div class="tab-icon-wrapper" :class="{ 'icon-bounce': activeTab === tab.key }">
              <i :class="tab.icon"></i>
            </div>
            <span class="tab-label">{{ tab.label }}</span>
            <div class="tab-active-indicator" v-if="activeTab === tab.key"></div>
            <span class="tab-ripple" v-if="rippleTab === tab.key"></span>
          </button>
        </div>
      </div>

      <!-- Tab Content Panels with Enhanced Transitions -->
      <transition name="tab-fade-slide" mode="out-in">
        <div :key="activeTab" class="tab-content">
          
          <!-- OVERVIEW TAB -->
          <div v-if="activeTab === 'overview'" class="tab-panel">
            <module-cards 
              :user-details="userDetails" 
              :search-query="searchQuery"
            />
          </div>

          <!-- SALES TAB -->
          <div v-else-if="activeTab === 'sales'" class="tab-panel">
            <div class="tab-header-section">
              <div class="header-left-side">
                <!-- <h2 class="section-title">Sales Dashboard</h2> -->
                <!-- <p class="section-subtitle">Real-time sales analytics and performance metrics</p> -->
              </div>
              <div class="header-right-side">
                <!-- Toggle Button to switch between Summary and Detail views -->
                <button class="btn-toggle-view" @click="toggleSalesView" :disabled="loadingComponent === 'sales'">
                  <i :class="showDetailSales ? 'fas fa-chart-simple' : 'fas fa-chart-line'"></i>
                  {{ showDetailSales ? 'Show Summary View' : 'Show Detail View' }}
                </button>
              </div>
            </div>
            
            <!-- Show ONLY ONE at a time - Summary View -->
            <div v-if="!showDetailSales" class="component-fade-in">
              <sales-dashboard2
                :user-details="userDetails" 
                :show-sales-dashboard="showSalesDashboard"
                @show-stat-popup="showStatPopup"
                @api-loading="handleApiLoading"
                @api-loaded="handleApiLoaded"
              />
            </div>
            
            <!-- Detail View (only when loaded and selected) -->
            <div v-else-if="showDetailSales && componentsLoaded.sales" class="component-fade-in">
              <sales-dashboard 
                :user-details="userDetails" 
                :show-sales-dashboard="showSalesDashboard"
                @show-stat-popup="showStatPopup"
                @api-loading="handleApiLoading"
                @api-loaded="handleApiLoaded"
              />
            </div>
            
            <!-- Loading state when detail view is being loaded -->
            <div v-else-if="showDetailSales && !componentsLoaded.sales && loadingComponent === 'sales'" class="lazy-load-prompt">
              <div class="prompt-card">
                <div class="prompt-icon">
                  <i class="fas fa-spinner fa-spin"></i>
                </div>
                <h4>Loading Detail Dashboard</h4>
                <p>Please wait while we load the detailed sales analytics...</p>
              </div>
            </div>
          </div>

          <!-- PERFORMANCE TAB -->
          <div v-else-if="activeTab === 'performance'" class="tab-panel">
            <div class="tab-header-section">
              <!-- <div class="header-left-side">
                <h2 class="section-title">Performance Analytics</h2>
                <p class="section-subtitle">Track team performance and individual achievements</p>
              </div> -->
              <div class="header-right-side">
                <!-- Toggle Button to switch between Summary and Detail views -->
                <button class="btn-toggle-view" @click="togglePerformanceView" :disabled="loadingComponent === 'topPerformance'">
                  <i :class="showDetailPerformance ? 'fas fa-chart-simple' : 'fas fa-trophy'"></i>
                  {{ showDetailPerformance ? 'Show Summary View' : 'Show Detail View' }}
                </button>
              </div>
            </div>
            
            <!-- Show ONLY ONE at a time - Summary View -->
            <div v-if="!showDetailPerformance" class="component-fade-in">
              <top-performance2
                :user-details="userDetails" 
                :show-top-performance="showTopPerformance"
                @api-loading="handleApiLoading"
                @api-loaded="handleApiLoaded"
              />
            </div>
            
            <!-- Detail View (only when loaded and selected) -->
            <div v-else-if="showDetailPerformance && componentsLoaded.topPerformance" class="component-fade-in">
              <top-performance 
                :user-details="userDetails" 
                :show-top-performance="showTopPerformance"
                @api-loading="handleApiLoading"
                @api-loaded="handleApiLoaded"
              />
            </div>
            
            <!-- Loading state when detail view is being loaded -->
            <div v-else-if="showDetailPerformance && !componentsLoaded.topPerformance && loadingComponent === 'topPerformance'" class="lazy-load-prompt">
              <div class="prompt-card">
                <div class="prompt-icon">
                  <i class="fas fa-spinner fa-spin"></i>
                </div>
                <h4>Loading Detail Dashboard</h4>
                <p>Please wait while we load the detailed performance analytics...</p>
              </div>
            </div>
          </div>

          <!-- PRODUCTS & INVENTORY TAB -->
          <div v-else-if="activeTab === 'products'" class="tab-panel">
            <div class="tab-header-section">
              <!-- <div class="header-left-side">
                <h2 class="section-title">Products & Inventory</h2>
                <p class="section-subtitle">Best sellers, inventory management, and product catalog</p>
              </div> -->
              <div class="header-right-side" v-if="showBestSellersDashboard && !componentsLoaded.bestSellers">
                <button class="btn-load-all" @click="loadComponent('bestSellers')" :disabled="loadingComponent === 'bestSellers'">
                  <i class="fas fa-crown"></i>
                  {{ loadingComponent === 'bestSellers' ? 'Loading...' : 'Load Dashboard' }}
                </button>
              </div>
            </div>
            
            <transition name="expand-fade" mode="out-in">
              <div v-if="componentsLoaded.bestSellers" class="component-fade-in">
                <best-sellers-dashboard 
                  :user-details="userDetails" 
                  :show-best-sellers-dashboard="showBestSellersDashboard"
                  @api-loading="handleApiLoading"
                  @api-loaded="handleApiLoaded"
                />
              </div>
              <div v-else-if="showBestSellersDashboard && !componentsLoaded.bestSellers" class="lazy-load-prompt">
                <div class="prompt-card">
                  <div class="prompt-icon">
                    <i class="fas fa-crown"></i>
                  </div>
                  <h4>Best Sellers Dashboard</h4>
                  <p>Load top products, categories, and inventory insights</p>
                  <button class="btn-load" @click="loadComponent('bestSellers')" :disabled="loadingComponent === 'bestSellers'">
                    <i class="fas fa-download" v-if="loadingComponent !== 'bestSellers'"></i>
                    <i class="fas fa-spinner fa-spin" v-else></i>
                    {{ loadingComponent === 'bestSellers' ? 'Loading...' : 'Load Dashboard' }}
                  </button>
                </div>
              </div>
            </transition>
          </div>

          <!-- SHIPPING TAB -->
          <div v-else-if="activeTab === 'shipping'" class="tab-panel">
            <div class="tab-header-section">
              <div class="header-left-side">
                <h2 class="section-title">Shipping & Logistics</h2>
                <p class="section-subtitle">Delivery tracking, receiving logs, and shipment management</p>
              </div>
              <div class="header-right-side" v-if="showShippingReceivingDashboard && !componentsLoaded.shipping">
                <button class="btn-load-all" @click="loadComponent('shipping')" :disabled="loadingComponent === 'shipping'">
                  <i class="fas fa-truck"></i>
                  {{ loadingComponent === 'shipping' ? 'Loading...' : 'Load Dashboard' }}
                </button>
              </div>
            </div>
            
            <transition name="expand-fade" mode="out-in">
              <div v-if="componentsLoaded.shipping" class="component-fade-in">
                <shipping-receiving-dashboard 
                  :user-details="userDetails" 
                  :show-shipping-receiving-dashboard="showShippingReceivingDashboard"
                  @api-loading="handleApiLoading"
                  @api-loaded="handleApiLoaded"
                />
              </div>
              <div v-else-if="showShippingReceivingDashboard && !componentsLoaded.shipping" class="lazy-load-prompt">
                <div class="prompt-card">
                  <div class="prompt-icon">
                    <i class="fas fa-truck"></i>
                  </div>
                  <h4>Shipping Dashboard</h4>
                  <p>Load delivery tracking, receiving logs, and shipment analytics</p>
                  <button class="btn-load" @click="loadComponent('shipping')" :disabled="loadingComponent === 'shipping'">
                    <i class="fas fa-download" v-if="loadingComponent !== 'shipping'"></i>
                    <i class="fas fa-spinner fa-spin" v-else></i>
                    {{ loadingComponent === 'shipping' ? 'Loading...' : 'Load Dashboard' }}
                  </button>
                </div>
              </div>
            </transition>
          </div>
        </div>
      </transition>

      <!-- Stat Popup Modal -->
      <b-modal v-model="showStatPopupModal" 
               :title="popupData.title" 
               size="lg" 
               hide-footer 
               centered
               class="stat-popup-modal"
               header-class="stat-modal-header"
               body-class="stat-modal-body">
        
        <div class="stat-popup-content">
          <div class="popup-main-value" :class="popupData.trendClass">
            {{ popupData.mainValue }}
          </div>
          
          <div class="popup-trend" v-if="popupData.trend">
            <i :class="popupData.trendIcon"></i>
            {{ popupData.trend }} vs previous period
          </div>

          <div class="popup-breakdown" v-if="popupData.breakdown">
            <h6>Breakdown</h6>
            <div class="breakdown-grid">
              <div v-for="(item, idx) in popupData.breakdown" :key="item.label" class="breakdown-item" :style="{ animationDelay: idx * 0.05 + 's' }">
                <span class="breakdown-label">{{ item.label }}:</span>
                <span class="breakdown-value">{{ item.value }}</span>
              </div>
            </div>
          </div>
        </div>

        <div class="popup-actions">
          <b-button variant="primary" @click="exportStatData(popupData.type)">
            <i class="fas fa-download"></i>
            Export Data
          </b-button>
          <b-button variant="outline-secondary" @click="showStatPopupModal = false">
            Close
          </b-button>
        </div>
      </b-modal>
    </div>
  </div>
</template>

<script>
import ModuleCards from './Homepagecomponents/ModuleCards.vue'
import SalesDashboard2 from './Homepagecomponents/SalesDashboard2.vue';
import TopPerformance2 from './Homepagecomponents/TopPerformance2.vue'
import moment from 'moment';

export default {
  name: "Home",
  components: {
    ModuleCards,
    TopPerformance2,
    SalesDashboard2,
  },
  data() {
    return {
      userDetails: {},
      searchQuery: "",
      activeTab: 'overview',
      rippleTab: null,
      showStatPopupModal: false,
      popupData: {
        title: '',
        type: '',
        mainValue: '',
        trend: null,
        trendClass: '',
        trendIcon: '',
        breakdown: null
      },
      
      // View toggles for mutual exclusivity
      showDetailSales: false,      // false = show summary (SalesDashboard2), true = show detail (SalesDashboard)
      showDetailPerformance: false, // false = show summary (TopPerformance2), true = show detail (TopPerformance)
      
      componentsLoaded: {
        topPerformance: false,
        sales: false,
        bestSellers: false,
        shipping: false
      },
      loadingComponent: null,
      
      isLoading: false,
      loadingMessage: 'Loading Dashboard',
      loadingProgress: 0,
      currentTipIndex: 0,
      loadingInterval: null,
      apiCallStack: 0,
      
      tips: [
        "📊 Fetching sales data...",
        "📈 Loading performance metrics...",
        "🎯 Analyzing top products...",
        "🚚 Retrieving shipping updates...",
        "💰 Calculating commissions...",
        "📦 Syncing inventory data...",
        "⚡ Almost there!",
        "✨ Preparing your dashboard..."
      ],
      
      tipInterval: null,
      msgInterval: null
    }
  },
  computed: {
    currentDate() {
      return moment().format('MMM DD, YYYY');
    },
    
    availableTabs() {
      const tabs = [
        { key: 'overview', label: 'Home', icon: 'fas fa-th-large' }
      ];
      
      if (this.showSalesDashboard) {
        tabs.push({ key: 'sales', label: 'Sales', icon: 'fas fa-chart-line' });
      }
      
      if (this.showTopPerformance) {
        tabs.push({ key: 'performance', label: 'Performance', icon: 'fas fa-trophy' });
      }
      
      if (this.showBestSellersDashboard) {
        tabs.push({ key: 'products', label: 'Best Sellers', icon: 'fas fa-box' });
      }
      
      if (this.showShippingReceivingDashboard) {
        tabs.push({ key: 'shipping', label: 'Shipping', icon: 'fas fa-truck' });
      }
      
      return tabs;
    },
    
    showSalesDashboard() {
      return this.showModule(['admin', 'accountmanager', 'officeuser', 'officemanager', 'locationadmin', 'salesperson', 'warehouseuser', 'warehousemanager']);
    },
    showBestSellersDashboard() {
      return this.showModule(['admin', 'accountmanager', 'officeuser', 'officemanager', 'locationadmin', 'salesperson']);
    },
    showShippingReceivingDashboard() {
      return this.showModule(['admin', 'accountmanager', 'officeuser', 'officemanager', 'locationadmin', 'locationstockperson', 'salesperson', 'warehouseuser', 'warehousemanager', 'housetruck', 'deliverytruck']);
    },
    showTopPerformance() {
      return this.showModule(['admin', 'accountmanager', 'officeuser', 'officemanager', 'locationadmin', 'salesperson']);
    },
    
    currentTip() {
      return this.tips[this.currentTipIndex];
    }
  },
  watch: {
    // Auto-load detail views when tabs are opened (optional)
    activeTab(newTab, oldTab) {
      if (newTab === 'sales' && !this.showDetailSales && !this.componentsLoaded.sales) {
        // Pre-load detail view in background when sales tab is opened
        this.loadComponent('sales').then(() => {
          // Don't auto-switch, let user decide with toggle button
        });
      } else if (newTab === 'performance' && !this.showDetailPerformance && !this.componentsLoaded.topPerformance) {
        // Pre-load detail view in background when performance tab is opened
        this.loadComponent('topPerformance').then(() => {
          // Don't auto-switch, let user decide with toggle button
        });
      }
    }
  },
  beforeDestroy() {
    if (this.loadingInterval) clearInterval(this.loadingInterval);
    if (this.tipInterval) clearInterval(this.tipInterval);
    if (this.msgInterval) clearInterval(this.msgInterval);
  },
  methods: {
    showModule(roles) {
      return roles.includes(this.userDetails.userrole);
    },
    formatUserRole(role) {
      if (!role) return 'Unknown Role';
      return role.charAt(0).toUpperCase() + role.slice(1).replace(/([A-Z])/g, ' $1');
    },
    
    switchTab(tabKey) {
      if (this.activeTab === tabKey) return;
      
      // Add ripple effect to the clicked tab
      this.rippleTab = tabKey;
      setTimeout(() => {
        this.rippleTab = null;
      }, 500);
      
      this.activeTab = tabKey;
    },
    
    // Toggle between summary and detail for Sales dashboard
    toggleSalesView() {
      if (this.showDetailSales) {
        // Switching FROM detail TO summary
        this.showDetailSales = false;
        this.$bvToast.toast('Switched to Summary View', {
          variant: 'info',
          autoHideDelay: 2000
        });
      } else {
        // Switching FROM summary TO detail
        if (!this.componentsLoaded.sales) {
          // Load the detail component first if not loaded
          this.loadComponent('sales').then(() => {
            this.showDetailSales = true;
            this.$bvToast.toast('Switched to Detail View', {
              variant: 'success',
              autoHideDelay: 2000
            });
          });
        } else {
          this.showDetailSales = true;
          this.$bvToast.toast('Switched to Detail View', {
            variant: 'success',
            autoHideDelay: 2000
          });
        }
      }
    },
    
    // Toggle between summary and detail for Performance dashboard
    togglePerformanceView() {
      if (this.showDetailPerformance) {
        // Switching FROM detail TO summary
        this.showDetailPerformance = false;
        this.$bvToast.toast('Switched to Summary View', {
          variant: 'info',
          autoHideDelay: 2000
        });
      } else {
        // Switching FROM summary TO detail
        if (!this.componentsLoaded.topPerformance) {
          // Load the detail component first if not loaded
          this.loadComponent('topPerformance').then(() => {
            this.showDetailPerformance = true;
            this.$bvToast.toast('Switched to Detail View', {
              variant: 'success',
              autoHideDelay: 2000
            });
          });
        } else {
          this.showDetailPerformance = true;
          this.$bvToast.toast('Switched to Detail View', {
            variant: 'success',
            autoHideDelay: 2000
          });
        }
      }
    },
    
    handleApiLoading(message = 'Loading data') {
      this.apiCallStack++;
      if (!this.isLoading) {
        this.startLoadingAnimation(message);
      } else {
        this.loadingMessage = message;
      }
    },
    
    handleApiLoaded() {
      this.apiCallStack--;
      if (this.apiCallStack <= 0) {
        this.stopLoadingAnimation(true);
      }
    },
    
    startLoadingAnimation(message = 'Loading Dashboard') {
      this.isLoading = true;
      this.loadingMessage = message;
      this.loadingProgress = 0;
      this.currentTipIndex = 0;
      
      this.loadingInterval = setInterval(() => {
        if (this.loadingProgress < 90) {
          this.loadingProgress += Math.random() * 10;
          if (this.loadingProgress > 90) this.loadingProgress = 90;
        }
      }, 300);
      
      this.tipInterval = setInterval(() => {
        if (this.currentTipIndex < this.tips.length - 1) {
          this.currentTipIndex++;
        }
      }, 2000);
      
      let messages = ['Loading Dashboard', 'Fetching Data', 'Processing Information', 'Updating Charts'];
      let msgIndex = 0;
      this.msgInterval = setInterval(() => {
        if (msgIndex < messages.length - 1) {
          msgIndex++;
          this.loadingMessage = messages[msgIndex];
        }
      }, 1800);
    },
    
    stopLoadingAnimation(success = true) {
      if (success) {
        this.loadingProgress = 100;
        setTimeout(() => {
          this.isLoading = false;
          if (this.loadingInterval) clearInterval(this.loadingInterval);
          if (this.tipInterval) clearInterval(this.tipInterval);
          if (this.msgInterval) clearInterval(this.msgInterval);
        }, 500);
      } else {
        this.isLoading = false;
        if (this.loadingInterval) clearInterval(this.loadingInterval);
        if (this.tipInterval) clearInterval(this.tipInterval);
        if (this.msgInterval) clearInterval(this.msgInterval);
      }
    },
    
    async loadComponent(componentName) {
      try {
        this.loadingComponent = componentName;
        this.handleApiLoading(`Loading ${this.getComponentName(componentName)}...`);
        
        let componentModule;
        
        switch(componentName) {
          case 'topPerformance':
            componentModule = await import('./Homepagecomponents/TopPerformance.vue');
            this.$options.components.TopPerformance = componentModule.default;
            this.componentsLoaded.topPerformance = true;
            break;
          case 'bestSellers':
            componentModule = await import('./Homepagecomponents/BestSellersDashboard.vue');
            this.$options.components.BestSellersDashboard = componentModule.default;
            this.componentsLoaded.bestSellers = true;
            break;
          case 'sales':
            componentModule = await import('./Homepagecomponents/SalesDashboard.vue');
            this.$options.components.SalesDashboard = componentModule.default;
            this.componentsLoaded.sales = true;
            break;
          case 'shipping':
            componentModule = await import('./Homepagecomponents/ShippingReceivingDashboard.vue');
            this.$options.components.ShippingReceivingDashboard = componentModule.default;
            this.componentsLoaded.shipping = true;
            break;
        }
        
        this.$bvToast.toast(`${this.getComponentName(componentName)} loaded successfully!`, {
          variant: 'success',
          autoHideDelay: 3000
        });
        
      } catch (error) {
        console.error(`Failed to load ${componentName}:`, error);
        this.$bvToast.toast(`Failed to load ${this.getComponentName(componentName)}`, {
          variant: 'danger',
          autoHideDelay: 5000
        });
      } finally {
        this.loadingComponent = null;
        this.handleApiLoaded();
      }
    },
    
    getComponentName(key) {
      const names = {
        topPerformance: 'Performance Dashboard',
        bestSellers: 'Best Sellers Dashboard',
        sales: 'Sales Dashboard',
        shipping: 'Shipping Dashboard'
      };
      return names[key] || key;
    },
    
    showStatPopup(data) {
      let popupData = {
        type: data.type,
        title: '',
        mainValue: '',
        trend: data.salesTrend,
        trendClass: this.getTrendClass(data.salesTrend),
        trendIcon: this.getTrendIcon(data.salesTrend),
        breakdown: null
      };
      
      switch(data.type) {
        case 'totalSales':
          popupData.title = 'Total Sales Overview';
          popupData.mainValue = `$${this.formatNumber(data.summaryTotals.totalSales)}`;
          popupData.breakdown = [
            { label: 'Regular Sales', value: `$${this.formatNumber(data.summaryTotals.regularSales || 0)}` },
            { label: 'Layaway Sales', value: `$${this.formatNumber(data.summaryTotals.layawaySales || 0)}` },
            { label: 'Average Order', value: `$${this.formatNumber(data.summaryTotals.averageOrderValue)}` }
          ];
          break;
        case 'totalOrders':
          popupData.title = 'Total Orders Overview';
          popupData.mainValue = this.formatNumber(data.summaryTotals.totalOrders);
          popupData.breakdown = [
            { label: 'Regular Orders', value: data.summaryTotals.regularOrders || 0 },
            { label: 'Layaway Orders', value: data.summaryTotals.layawayOrders || 0 }
          ];
          break;
        case 'commission':
          popupData.title = 'Commission Overview';
          popupData.mainValue = `$${this.formatNumber(data.summaryTotals.totalCommission)}`;
          popupData.breakdown = [
            { label: 'Base Commission', value: `$${this.formatNumber(data.summaryTotals.totalCommission * 0.7)}` },
            { label: 'Bonus Commission', value: `$${this.formatNumber(data.summaryTotals.totalCommission * 0.3)}` }
          ];
          break;
        case 'refund':
          popupData.title = 'Refund Overview';
          popupData.mainValue = `$${this.formatNumber(data.summaryTotals.totalRefund)}`;
          popupData.breakdown = [
            { label: 'Total Refunded Amount', value: `$${this.formatNumber(data.summaryTotals.totalRefund)}` },
            { label: 'Refunded Items', value: data.summaryTotals.refundItemsCount || 0 }
          ];
          break;
      }
      
      this.popupData = popupData;
      this.showStatPopupModal = true;
    },
    
    formatNumber(num) {
      if (!num && num !== 0) return '0.00';
      return num.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    },
    
    getTrendClass(trend) {
      if (!trend && trend !== 0) return '';
      return trend >= 0 ? 'trend-positive' : 'trend-negative';
    },
    
    getTrendIcon(trend) {
      if (!trend && trend !== 0) return 'fas fa-minus';
      return trend >= 0 ? 'fas fa-arrow-up' : 'fas fa-arrow-down';
    },
    
    exportStatData(statType) {
      console.log(`Exporting ${statType} data`);
      this.$bvToast.toast(`Exporting ${statType} data...`, { variant: 'info' });
    }
  },
  created() { 
    let userDetails = localStorage.userDetails;
    if (userDetails) {
      this.userDetails = JSON.parse(userDetails);
    }
  }
}
</script>

<style scoped>
.fm-container {
  position: relative;
  min-height: 100vh;
  width: 100%;
  overflow-x: hidden;
}

/* Background Image */
.background-image {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-image: url('https://m.media-amazon.com/images/I/81eSegKonhL._AC_SL1500_.jpg');
  background-size: cover;
  background-position: center;
  background-repeat: no-repeat;
  filter: blur(8px);
  opacity: 0.7;
  z-index: -1;
  transform: scale(1.1);
  transition: filter 0.3s ease;
}

.background-image.blur-active {
  filter: blur(12px);
}

/* Loading Overlay Animation */
.loading-overlay-enter-active,
.loading-overlay-leave-active {
  transition: opacity 0.4s ease;
}

.loading-overlay-enter,
.loading-overlay-leave-to {
  opacity: 0;
}

.loading-overlay-enter-to,
.loading-overlay-leave {
  opacity: 1;
}

.loading-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.85);
  backdrop-filter: blur(20px);
  z-index: 1000;
  display: flex;
  align-items: center;
  justify-content: center;
}

@keyframes fadeIn {
  from { opacity: 0; }
  to { opacity: 1; }
}

.loading-card {
  background: linear-gradient(135deg, rgba(255, 255, 255, 0.95), rgba(245, 245, 245, 0.95));
  border-radius: 24px;
  padding: 48px 56px;
  text-align: center;
  box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.5);
  max-width: 450px;
  width: 90%;
  animation: cardPop 0.5s cubic-bezier(0.68, -0.55, 0.265, 1.55);
}

@keyframes cardPop {
  from {
    opacity: 0;
    transform: scale(0.8);
  }
  to {
    opacity: 1;
    transform: scale(1);
  }
}

.loading-animation {
  margin-bottom: 32px;
}

.loading-ring {
  position: relative;
  width: 100px;
  height: 100px;
  margin: 0 auto;
  display: flex;
  align-items: center;
  justify-content: center;
}

.ring {
  position: absolute;
  width: 100%;
  height: 100%;
  border: 4px solid transparent;
  border-radius: 50%;
  animation: ringRotate 1.5s linear infinite;
}

.ring:nth-child(1) {
  border-top-color: #4299e1;
  border-left-color: #4299e1;
  animation-duration: 1s;
}

.ring:nth-child(2) {
  border-right-color: #48bb78;
  border-bottom-color: #48bb78;
  width: 80%;
  height: 80%;
  top: 10%;
  left: 10%;
  animation-duration: 1.2s;
  animation-direction: reverse;
}

.ring:nth-child(3) {
  border-left-color: #ed8936;
  border-bottom-color: #ed8936;
  width: 60%;
  height: 60%;
  top: 20%;
  left: 20%;
  animation-duration: 0.8s;
}

@keyframes ringRotate {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

.loading-logo {
  font-size: 32px;
  font-weight: bold;
  color: #2d3748;
  animation: logoPulse 1.5s ease-in-out infinite;
  z-index: 1;
}

.loading-logo i {
  font-size: 40px;
  background: linear-gradient(135deg, #4299e1, #48bb78);
  -webkit-background-clip: text;
  background-clip: text;
  color: transparent;
}

@keyframes logoPulse {
  0%, 100% { transform: scale(1); opacity: 0.7; }
  50% { transform: scale(1.1); opacity: 1; }
}

.loading-text {
  margin-bottom: 24px;
}

.loading-text h3 {
  color: #2d3748;
  font-size: 1.3rem;
  margin-bottom: 8px;
  display: inline-block;
}

.loading-dots {
  display: inline-flex;
  margin-left: 8px;
}

.loading-dots .dot {
  width: 6px;
  height: 6px;
  background: #4299e1;
  border-radius: 50%;
  margin: 0 2px;
  animation: dotBounce 1.4s ease-in-out infinite;
}

.loading-dots .dot:nth-child(1) { animation-delay: 0s; }
.loading-dots .dot:nth-child(2) { animation-delay: 0.2s; }
.loading-dots .dot:nth-child(3) { animation-delay: 0.4s; }

@keyframes dotBounce {
  0%, 60%, 100% { transform: translateY(0); }
  30% { transform: translateY(-8px); }
}

.loading-progress {
  width: 100%;
  height: 4px;
  background: #e2e8f0;
  border-radius: 4px;
  overflow: hidden;
  margin-bottom: 20px;
}

.progress-bar {
  height: 100%;
  background: linear-gradient(90deg, #4299e1, #48bb78, #ed8936, #4299e1);
  background-size: 200% 100%;
  animation: progressShine 1.5s linear infinite;
  transition: width 0.3s ease;
  border-radius: 4px;
}

@keyframes progressShine {
  0% { background-position: 200% 0; }
  100% { background-position: -200% 0; }
}

.loading-tips {
  margin-top: 20px;
}

.tip-text {
  color: #718096;
  font-size: 0.9rem;
  animation: tipFade 0.5s ease;
}

@keyframes tipFade {
  from {
    opacity: 0;
    transform: translateY(10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

/* Content Wrapper */
.content-wrapper {
  position: relative;
  z-index: 1;
  padding: 24px 32px;
  max-width: 100%;
  transition: filter 0.3s ease;
}

.content-wrapper.blur-content {
  filter: blur(5px);
  pointer-events: none;
}

/* Fade Slide Down Animation */
.fade-slide-down-enter-active {
  animation: fadeSlideDown 0.6s cubic-bezier(0.2, 0.9, 0.4, 1.1);
}

@keyframes fadeSlideDown {
  from {
    opacity: 0;
    transform: translateY(-30px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

/* Dashboard Header */
.dashboard-header {
  background: rgba(255, 255, 255, 0.75);
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 28px;
  flex-wrap: wrap;
  gap: 16px;
  padding: 16px 16px;
  border-radius: 24px;
}

.greeting-badge {
  display: flex;
  align-items: center;
  gap: 12px;
}

.greeting-icon {
  font-size: 40px;
  animation: wave 2s ease-in-out infinite;
  transform-origin: 70% 70%;
  display: inline-block;
}

@keyframes wave {
  0% { transform: rotate(0deg); }
  10% { transform: rotate(14deg); }
  20% { transform: rotate(-8deg); }
  30% { transform: rotate(14deg); }
  40% { transform: rotate(-4deg); }
  50% { transform: rotate(10deg); }
  60% { transform: rotate(0deg); }
  100% { transform: rotate(0deg); }
}

.dashboard-title {
  font-size: 28px;
  font-weight: 800;
  background: linear-gradient(135deg, #1e293b, #2d3748);
  -webkit-background-clip: text;
  background-clip: text;
  color: transparent;
  margin-bottom: 4px;
}

.dashboard-subtitle {
  color: #64748b;
  font-size: 14px;
  font-weight: 500;
}

.header-right {
  display: flex;
  align-items: center;
  gap: 12px;
}

.user-role-badge {
  display: flex;
  align-items: center;
  gap: 8px;
  background: rgba(255, 255, 255, 0.9);
  padding: 8px 18px;
  border-radius: 40px;
  font-weight: 600;
  font-size: 13px;
  color: #00698B;
  backdrop-filter: blur(5px);
  border: 1px solid rgba(0, 105, 139, 0.2);
}

.date-badge {
  display: flex;
  align-items: center;
  gap: 8px;
  background: rgba(255, 255, 255, 0.7);
  padding: 8px 16px;
  border-radius: 40px;
  font-size: 13px;
  color: #475569;
  backdrop-filter: blur(5px);
  border: 1px solid rgba(0, 0, 0, 0.05);
}

/* Tab Bar Styles */
.tab-bar-container {
  background: rgba(255, 255, 255, 0.75);
  backdrop-filter: blur(10px);
  border-radius: 60px;
  margin-bottom: 28px;
  padding: 6px;
  border: 1px solid rgba(255, 255, 255, 0.3);
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.05);
}

.tab-bar {
  display: flex;
  gap: 6px;
  overflow-x: auto;
  scrollbar-width: none;
  position: relative;
}

.tab-bar::-webkit-scrollbar {
  display: none;
}

.tab-btn {
  position: relative;
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px 28px;
  background: transparent;
  border: none;
  border-radius: 40px;
  font-size: 14px;
  font-weight: 600;
  color: #64748b;
  cursor: pointer;
  transition: all 0.3s cubic-bezier(0.2, 0.9, 0.4, 1.1);
  white-space: nowrap;
  overflow: hidden;
}

/* Ripple Effect */
.tab-ripple {
  position: absolute;
  top: 50%;
  left: 50%;
  width: 0;
  height: 0;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.5);
  transform: translate(-50%, -50%);
  animation: rippleAnim 0.5s ease-out forwards;
  pointer-events: none;
}

@keyframes rippleAnim {
  0% {
    width: 0;
    height: 0;
    opacity: 0.5;
  }
  100% {
    width: 200px;
    height: 200px;
    opacity: 0;
  }
}

.tab-icon-wrapper {
  display: flex;
  align-items: center;
  justify-content: center;
  transition: transform 0.3s cubic-bezier(0.34, 1.56, 0.64, 1);
}

.tab-icon-wrapper.icon-bounce {
  animation: iconBounce 0.4s cubic-bezier(0.34, 1.56, 0.64, 1);
}

@keyframes iconBounce {
  0% { transform: scale(1); }
  50% { transform: scale(1.2); }
  100% { transform: scale(1); }
}

.tab-btn i {
  font-size: 18px;
  transition: transform 0.2s ease;
}

.tab-btn:hover {
  background: rgba(0, 105, 139, 0.08);
  color: #00698B;
  transform: translateY(-2px);
}

.tab-btn:hover .tab-icon-wrapper {
  transform: translateY(-2px);
}

.tab-btn.active {
  background: #00698B;
  color: white;
  box-shadow: 0 4px 15px rgba(0, 105, 139, 0.35);
}

.tab-active-indicator {
  position: absolute;
  bottom: -2px;
  left: 50%;
  transform: translateX(-50%);
  width: 30px;
  height: 3px;
  background: white;
  border-radius: 3px;
  animation: indicatorPulse 0.4s ease-out;
}

@keyframes indicatorPulse {
  0% {
    width: 0;
    opacity: 0;
  }
  100% {
    width: 30px;
    opacity: 1;
  }
}

/* Tab Content Transition */
.tab-fade-slide-enter-active {
  animation: tabSlideIn 0.4s cubic-bezier(0.2, 0.9, 0.4, 1.1);
}

.tab-fade-slide-leave-active {
  animation: tabSlideOut 0.3s cubic-bezier(0.2, 0.9, 0.4, 1.1);
}

@keyframes tabSlideIn {
  from {
    opacity: 0;
    transform: translateX(15px);
  }
  to {
    opacity: 1;
    transform: translateX(0);
  }
}

@keyframes tabSlideOut {
  from {
    opacity: 1;
    transform: translateX(0);
  }
  to {
    opacity: 0;
    transform: translateX(-15px);
  }
}

.tab-content {
  margin-top: 8px;
}

.tab-panel {
  animation: contentFadeIn 0.4s ease-out;
}

@keyframes contentFadeIn {
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
}

/* Section Title Animations */
.section-title {
  font-size: 22px;
  font-weight: 700;
  color: #1e293b;
  margin-bottom: 6px;
  position: relative;
  display: inline-block;
}

.section-title::after {
  content: '';
  position: absolute;
  bottom: -4px;
  left: 0;
  width: 0;
  height: 3px;
  background: linear-gradient(90deg, #00698B, #0088B0);
  border-radius: 3px;
  animation: titleUnderline 0.6s ease-out forwards;
}

@keyframes titleUnderline {
  from {
    width: 0;
    opacity: 0;
  }
  to {
    width: 50px;
    opacity: 1;
  }
}

.section-subtitle {
  color: #64748b;
  font-size: 13px;
  animation: subtitleFade 0.5s ease-out 0.2s both;
}

@keyframes subtitleFade {
  from {
    opacity: 0;
    transform: translateY(5px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.tab-header-section {
  background-color: rgba(255, 255, 255, 0.75);
  border-radius: 24px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 24px;
  flex-wrap: wrap;
  gap: 16px;
  padding: 16px 16px;
}

.header-left-side h2 {
  font-size: 22px;
  font-weight: 700;
  color: #1e293b;
  margin-bottom: 6px;
  padding: 20px;
}

.header-left-side p {
  color: #64748b;
  font-size: 13px;
}

/* Toggle View Button Styles */
.btn-toggle-view {
  background: linear-gradient(135deg, #00698B, #0088B0);
  border: none;
  padding: 10px 24px;
  border-radius: 40px;
  color: white;
  font-weight: 600;
  font-size: 13px;
  cursor: pointer;
  transition: all 0.3s cubic-bezier(0.2, 0.9, 0.4, 1.1);
  display: flex;
  align-items: center;
  gap: 10px;
  white-space: nowrap;
}

.btn-toggle-view:hover {
  transform: translateY(-2px);
  box-shadow: 0 6px 20px rgba(0, 105, 139, 0.3);
}

.btn-toggle-view:active {
  transform: translateY(0);
}

.btn-toggle-view:disabled {
  opacity: 0.6;
  cursor: not-allowed;
  transform: none;
}

.btn-toggle-view i {
  transition: transform 0.2s ease;
}

.btn-toggle-view:hover i {
  transform: scale(1.1);
}

.btn-load-all {
  background: linear-gradient(135deg, #00698B, #0088B0);
  border: none;
  padding: 10px 24px;
  border-radius: 40px;
  color: white;
  font-weight: 600;
  font-size: 13px;
  cursor: pointer;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  gap: 10px;
}

.btn-load-all:hover {
  transform: translateY(-2px);
  box-shadow: 0 6px 20px rgba(0, 105, 139, 0.3);
}

.btn-load-all:disabled {
  opacity: 0.6;
  cursor: not-allowed;
  transform: none;
}

/* Expand Fade Transition */
.expand-fade-enter-active {
  animation: expandFadeIn 0.5s cubic-bezier(0.2, 0.9, 0.4, 1.1);
}

.expand-fade-leave-active {
  animation: expandFadeOut 0.3s ease-out;
}

@keyframes expandFadeIn {
  from {
    opacity: 0;
    transform: scaleY(0.8);
    transform-origin: top;
  }
  to {
    opacity: 1;
    transform: scaleY(1);
    transform-origin: top;
  }
}

@keyframes expandFadeOut {
  from {
    opacity: 1;
    transform: scaleY(1);
    transform-origin: top;
  }
  to {
    opacity: 0;
    transform: scaleY(0.8);
    transform-origin: top;
  }
}

/* Lazy Load Prompt */
.lazy-load-prompt {
  margin-top: 20px;
}

.prompt-card {
  background: rgba(255, 255, 255, 0.9);
  backdrop-filter: blur(10px);
  border-radius: 24px;
  padding: 40px;
  text-align: center;
  border: 1px solid rgba(0, 105, 139, 0.15);
  transition: all 0.3s ease;
  animation: promptPulse 2s ease-in-out infinite;
}

@keyframes promptPulse {
  0%, 100% {
    box-shadow: 0 0 0 0 rgba(0, 105, 139, 0.1);
  }
  50% {
    box-shadow: 0 0 0 15px rgba(0, 105, 139, 0);
  }
}

.prompt-card:hover {
  transform: translateY(-4px);
  box-shadow: 0 20px 40px -12px rgba(0, 0, 0, 0.15);
}

.prompt-icon {
  width: 80px;
  height: 80px;
  background: linear-gradient(135deg, rgba(0, 105, 139, 0.1), rgba(0, 136, 176, 0.1));
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto 20px;
  animation: iconFloat 3s ease-in-out infinite;
}

@keyframes iconFloat {
  0%, 100% {
    transform: translateY(0);
  }
  50% {
    transform: translateY(-8px);
  }
}

.prompt-icon i {
  font-size: 40px;
  color: #00698B;
}

.prompt-card h4 {
  font-size: 20px;
  font-weight: 700;
  color: #1e293b;
  margin-bottom: 10px;
}

.prompt-card p {
  color: #64748b;
  font-size: 14px;
  margin-bottom: 24px;
}

.btn-load {
  background: linear-gradient(135deg, #00698B, #0088B0);
  border: none;
  padding: 12px 32px;
  border-radius: 40px;
  color: white;
  font-weight: 600;
  font-size: 14px;
  cursor: pointer;
  transition: all 0.2s ease;
  display: inline-flex;
  align-items: center;
  gap: 10px;
}

.btn-load:hover {
  transform: translateY(-2px);
  box-shadow: 0 6px 20px rgba(0, 105, 139, 0.3);
}

.btn-load:disabled {
  opacity: 0.6;
  cursor: not-allowed;
  transform: none;
}

/* Component Fade-in */
.component-fade-in {
  animation: slideUpFade 0.5s ease-out;
}

@keyframes slideUpFade {
  from {
    opacity: 0;
    transform: translateY(30px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

/* Stat Popup Modal */
.stat-popup-modal ::v-deep .stat-modal-header {
  background: linear-gradient(135deg, #2d3748, #4a5568);
  color: white;
  border-bottom: none;
  border-radius: 16px 16px 0 0;
  padding: 20px 24px;
}

.stat-popup-modal ::v-deep .stat-modal-body {
  padding: 0;
  background: rgba(249, 250, 251, 0.98);
}

.stat-popup-content {
  padding: 32px;
  text-align: center;
}

.popup-main-value {
  font-size: 3rem;
  font-weight: 800;
  color: #2d3748;
  margin-bottom: 16px;
  animation: valuePop 0.4s cubic-bezier(0.34, 1.56, 0.64, 1);
}

@keyframes valuePop {
  from {
    opacity: 0;
    transform: scale(0.8);
  }
  to {
    opacity: 1;
    transform: scale(1);
  }
}

.popup-trend {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  font-size: 1rem;
  font-weight: 600;
  margin-bottom: 24px;
  animation: trendSlide 0.4s ease-out 0.1s both;
}

@keyframes trendSlide {
  from {
    opacity: 0;
    transform: translateY(-10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.popup-breakdown {
  margin: 24px 0;
}

.popup-breakdown h6 {
  color: #4a5568;
  margin-bottom: 16px;
  font-weight: 700;
  font-size: 16px;
}

.breakdown-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 12px;
}

.breakdown-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 12px 16px;
  background: rgba(255, 255, 255, 0.9);
  border-radius: 12px;
  border-left: 4px solid #00698B;
  opacity: 0;
  transform: translateX(-20px);
  animation: breakdownItemSlide 0.3s ease-out forwards;
}

.breakdown-item:nth-child(1) { animation-delay: 0.05s; }
.breakdown-item:nth-child(2) { animation-delay: 0.1s; }
.breakdown-item:nth-child(3) { animation-delay: 0.15s; }
.breakdown-item:nth-child(4) { animation-delay: 0.2s; }

@keyframes breakdownItemSlide {
  from {
    opacity: 0;
    transform: translateX(-20px);
  }
  to {
    opacity: 1;
    transform: translateX(0);
  }
}

.breakdown-label {
  color: #718096;
  font-weight: 600;
  font-size: 13px;
}

.breakdown-value {
  color: #2d3748;
  font-weight: 700;
  font-size: 14px;
  transition: transform 0.2s ease;
}

.breakdown-item:hover .breakdown-value {
  transform: scale(1.05);
  color: #00698B;
}

.popup-actions {
  padding: 20px 24px;
  border-top: 1px solid rgba(226, 232, 240, 0.8);
  display: flex;
  justify-content: center;
  gap: 12px;
  background: rgba(255, 255, 255, 0.9);
}

.trend-positive {
  color: #10b981;
}

.trend-negative {
  color: #ef4444;
}

/* Responsive Design */
@media (max-width: 1024px) {
  .content-wrapper {
    padding: 20px 24px;
  }
}

@media (max-width: 768px) {
  .content-wrapper {
    padding: 16px;
  }
  
  .dashboard-header {
    flex-direction: column;
    align-items: flex-start;
  }
  
  .dashboard-title {
    font-size: 24px;
  }
  
  .greeting-icon {
    font-size: 32px;
  }
  
  .tab-bar-container {
    border-radius: 20px;
  }
  
  .tab-btn {
    padding: 10px 20px;
  }
  
  .tab-label {
    display: none;
  }
  
  .tab-btn i {
    font-size: 20px;
    margin: 0;
  }
  
  .tab-btn.active .tab-label {
    display: inline;
  }
  
  .tab-btn.active i {
    margin-right: 8px;
  }
  
  .tab-header-section {
    flex-direction: column;
    align-items: flex-start;
  }
  
  .btn-toggle-view, .btn-load-all {
    width: 100%;
    justify-content: center;
  }
  
  .popup-main-value {
    font-size: 2rem;
  }
  
  .breakdown-grid {
    grid-template-columns: 1fr;
  }
  
  .loading-card {
    padding: 32px 24px;
  }
  
  .loading-ring {
    width: 80px;
    height: 80px;
  }
  
  .loading-logo i {
    font-size: 32px;
  }
}

@media (max-width: 480px) {
  .background-image {
    filter: blur(4px);
  }
  
  .dashboard-title {
    font-size: 20px;
  }
  
  .user-role-badge, .date-badge {
    padding: 6px 12px;
    font-size: 11px;
  }
  
  .prompt-card {
    padding: 28px 20px;
  }
  
  .prompt-icon {
    width: 60px;
    height: 60px;
  }
  
  .prompt-icon i {
    font-size: 28px;
  }
  
  .prompt-card h4 {
    font-size: 18px;
  }
  
  .stat-popup-content {
    padding: 24px;
  }
}
</style>


// WEBPACK FOOTER //
// src/components/Home.vue